import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "@components/Layouts/Carousel/RelatedArticleCarousel.css";

import enabledArrow from "@assets/actions/enable-carousel-button.svg";
import ArticleCard from "@blocks/Card/ArticleCard";
import Heading from "@elements/Typography/Heading";
import { ProcessedArticleData } from "@helper/transformer/useOsResponse";
import { useTMCustomEventAnalyticsCall } from "@hooks/useAnalyticsCall/useTMCustomEventAnalyticsCall";
import { useEffect, useRef } from "react";
import Slider, { Settings } from "react-slick";
import { twMerge } from "tailwind-merge";

type RelatedArticleCarouselProps = Readonly<{
  testId?: string;
  rootClassName?: string;
  dataSectionName?: string;
  relatedArticles: ProcessedArticleData[];
  source?: string;
  middleArticle?: boolean;
  loadGaEvent?: boolean;
}>;

type WithGaEventLoad = {
  loadGaEvent: true;
  pageUrl: string;
};

type WithoutGaEventLoad = {
  loadGaEvent?: false | undefined;
  pageUrl?: string;
};

export type CarousalProps = RelatedArticleCarouselProps &
  (WithGaEventLoad | WithoutGaEventLoad);

export default function RelatedArticleCarousel({
  testId,
  rootClassName,
  dataSectionName,
  relatedArticles,
  source,
  middleArticle = false,
  loadGaEvent = false,
  pageUrl,
}: CarousalProps): React.ReactElement {
  // eslint-disable-next-line
  const SliderComponent: typeof Slider =
    // eslint-disable-next-line
    // @ts-expect-error
    typeof window === "undefined" ? Slider.default : Slider;

  const sliderRef = useRef<Slider>(null);
  const next = () => {
    if (sliderRef.current) {
      sliderRef.current.slickNext();
    }
  };

  const previous = () => {
    if (sliderRef.current) {
      sliderRef.current.slickPrev();
    }
  };
  // Need to do this as workaroudn known issue, https://github.com/akiran/react-slick/issues/1130
  const SlickButtonFix = (props: {
    children: React.ReactNode;
    slideCount?: number;
    currentSlide?: number;
  }) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { children, currentSlide, slideCount, ...others } = props;
    return <span {...others}>{children}</span>;
  };
  const totalOptions = relatedArticles.length;
  const showArrows = totalOptions > (middleArticle ? 2 : 3) ? true : false;
  const settings: Settings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToScroll: 1,
    slidesToShow: middleArticle ? 2 : 3.3,
    className: twMerge(
      "related-articles lg:grid-cols-1 lg:gap-6 lg:block lg:-mr-5",
      middleArticle ? "mid-related-articles" : "bottom-related-articles",
    ),
    arrows: showArrows,
    prevArrow: (
      <SlickButtonFix>
        <button onClick={previous} data-testid="prev-button">
          <img
            width={20}
            height={20}
            src={enabledArrow}
            alt="prev-arrow"
            className="-scale-x-100 w-10"
          />
        </button>
      </SlickButtonFix>
    ),
    nextArrow: (
      <SlickButtonFix>
        <button onClick={next} data-testid="next-button">
          <img
            width={20}
            height={20}
            src={enabledArrow}
            alt="next-arrow"
            className="w-10"
          />
        </button>
      </SlickButtonFix>
    ),
    swipeToSlide: true,
    responsive: [
      {
        breakpoint: 991,
        settings: {
          slidesToShow: 1.5,
          swipeToSlide: true,
          arrows: false,
        },
      },
    ],
  };

  const TmCustomGaEvent = useTMCustomEventAnalyticsCall();
  const hasRun = useRef(false);
  useEffect(() => {
    if (loadGaEvent && !hasRun.current) {
      TmCustomGaEvent({
        event: "customEvent",
        eventCategory: middleArticle
          ? "related-story-middle"
          : "related-story-bottom",
        eventAction: "Visible",
        eventLabel: origin + pageUrl,
      });
      hasRun.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={rootClassName}
      data-testid={testId}
      data-section-name={dataSectionName}
    >
      <Heading
        Element="h2"
        className="font-primary font-bold text-md mb-5 text-gray-100"
      >
        தொடர்புடைய செய்திகள்
      </Heading>
      <SliderComponent {...settings}>
        {relatedArticles.map((item) => {
          return (
            <ArticleCard
              key={item.id}
              displayWidth={middleArticle ? 175 : 235}
              displayHeight={middleArticle ? 90 : 157}
              overrideStyle={{
                cardWrapper: twMerge(
                  "article-card-wrapper flex items-center w-full pr-xs pt-0 border-0",
                  middleArticle
                    ? "!flex-row-reverse pb-0 items-start gap-2.5 !pr-0"
                    : "flex-col justify-center",
                ),
                contentWrapper: twMerge("w-full", middleArticle && "w-[170px]"),
                titleStyle: twMerge(
                  "h-13.5 text-gray-100 hover:text-gray-100",
                  !middleArticle && "overflow-hidden overflow-ellipsis",
                ),
                imageWrapperStyle: twMerge(
                  "lg:w-[235px]",
                  middleArticle && "!w-[175px] !h-[90px]",
                ),
              }}
              elements={{
                hasParagraph: false,
                hasDate: middleArticle ? false : true,
              }}
              refMedium={source}
              {...item}
            />
          );
        })}
      </SliderComponent>
    </div>
  );
}
